import * as types from './types';
import moment from 'moment';

// Convenience method for optimistically updating redux when a number of
// intervals needs to be updated. A temporary schedule is
// created which is passed to combinedScheduleUpdated.
// If clearGaps is set to true (or ommitted), all periods are cleared between
// updateIntervalStart and updateIntervalEnd that are not covered by the
// intervals.
export const combinedScheduleIntervalsUpdatedOptimistically = (
  id,
  ownerType,
  intervals,
  updateIntervalStart,
  updateIntervalEnd,
  { clearGaps = true }
) => {
  const withoutGaps = [];

  const firstInterval = intervals[0];
  const lastInterval = intervals.at(-1);
  const createInterval = (start_time, end_time) => {
    if (moment(start_time).isBefore(end_time)) {
      withoutGaps.push({
        start_time,
        end_time,
        assigned_function_ids: []
      });
    }
  };

  if (firstInterval) {
    clearGaps && createInterval(updateIntervalStart, firstInterval.start_time);

    for (const [index, interval] of intervals.entries()) {
      if (index === 0) {
        withoutGaps.push(interval);
        continue;
      }
      const previousInterval = intervals[index - 1];
      clearGaps && createInterval(previousInterval.end_time, interval.start_time);
      withoutGaps.push(interval);
    }
    clearGaps && createInterval(lastInterval.end_time, updateIntervalEnd);
  } else {
    clearGaps && createInterval(updateIntervalStart, updateIntervalEnd);
  }

  // mark all these intervals as transient, because they're only present in
  // memory, not in the backend
  const withoutGapsTransient = withoutGaps.map(i => ({
    ...i,
    transient: true,
    annotations: {}
  }));

  const schedule = {
    owner_id: id,
    owner_type: ownerType,
    start_time: updateIntervalStart,
    end_time: updateIntervalEnd,
    intervals: withoutGapsTransient
  };

  return combinedScheduleUpdated(id, ownerType, schedule);
};

const combinedScheduleUpdated = (
  id,
  ownerType,
  schedule,
  { mergeMode = true, overwriteUnsavedChanges = true } = {}
) => ({
  type: types.COMBINED_SCHEDULE_UPDATED,
  timestamp: Date.now(),
  mergeMode,
  overwriteUnsavedChanges,
  payload: {
    id,
    ownerType,
    schedule
  }
});

// For batching multiple updates. Improves performance because there is only 1
// state change. It returns a set of actions of type COMBINED_SCHEDULE_UPDATED
const batchCombinedScheduleUpdated = (schedules, { mergeMode = true, overwriteUnsavedChanges = true } = {}) => ({
  type: types.BATCH_COMBINED_SCHEDULE_UPDATED,
  timestamp: Date.now(),
  mergeMode,
  overwriteUnsavedChanges,
  payload: {
    actions: schedules.map(schedule =>
      combinedScheduleUpdated(schedule.owner_id, schedule.owner_type, schedule, {
        mergeMode,
        overwriteUnsavedChanges
      })
    )
  }
});

const fetchCombinedScheduleStart = () => ({
  type: types.START
});

const fetchCombinedScheduleSuccess = () => ({
  type: types.SUCCESS
});

const fetchFailed = error => ({
  type: types.ERROR,
  error,
  success: false,
  timestamp: Date.now()
});

// Takes the schedule of the owner, and splits the interval that overlaps with
// `time` at that point. This is used in e.g. the DutyPlanner for carving out an
// interval from an existing one in order to assign an availability code.
const splitCombinedScheduleIntervals = (ownerId, ownerType, time) => ({
  type: types.SPLIT_COMBINED_SCHEDULE_INTERVAL,
  payload: { ownerId, ownerType, time }
});

const scheduleWarningsReceived = schedule_warnings => ({
  type: types.SCHEDULE_WARNINGS_RECEIVED,
  success: false,
  payload: { scheduleWarnings: schedule_warnings }
});

const resetState = () => ({
  type: types.RESET_STATE
});

const scheduleWarningsCleared = () => ({
  type: types.SCHEDULE_WARNINGS_CLEARED,
  success: true
});

const combinedScheduleValidationErrorsReceived = (id, ownerType, startTime, endTime, validationErrors) => ({
  type: types.COMBINED_SCHEDULE_VALIDATION_ERRORS_RECEIVED,
  success: false,
  payload: { id, ownerType, startTime, endTime, validationErrors }
});

export {
  combinedScheduleUpdated,
  batchCombinedScheduleUpdated,
  fetchCombinedScheduleStart,
  fetchCombinedScheduleSuccess,
  fetchFailed,
  splitCombinedScheduleIntervals,
  scheduleWarningsReceived,
  scheduleWarningsCleared,
  combinedScheduleValidationErrorsReceived,
  resetState
};
